import { memo, useCallback, useMemo, useSyncExternalStore } from 'react';
import { IconTypography } from '@tabler/icons-react';
import { Popover, Transition } from '@headlessui/react';
import PlayerMenuTooltip from '../../../PlayerMenus/PlayerMenuTooltip';
import { CAPTION_LINE_CONFIG_OPTIONS } from '../../constants';
import { classnames } from '@/libs/utils';
import FontSelector from '@/Pages/Clip/SideBar/FontSelector/FontSelector';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { updateCaptionStyles } from '@/stores/clip';
import { ColorPicker } from '@/components/atoms/ColorPicker/ColorPicker';
import { brandKit } from '@/stores/brandKit';
import Toggle from '@/components/atoms/Toggle';
import { CaptionLineConfig } from '@/domains/asset';

function CaptionTypographyPopover() {
  const { clipId, clipData } = useClipsContext();
  const brandKitStore = useSyncExternalStore(brandKit.subscribe, brandKit.getSnapshot);

  const onFontSizeChange = useCallback(
    e => {
      const newFontSize = Number(e.target.value);
      updateCaptionStyles(clipId, { scale: 1, font_size: newFontSize }, false);
    },
    [clipId]
  );

  const onFontSizeChangeComplete = useCallback(
    e => {
      const newFontSize = Number(e.target.value);
      updateCaptionStyles(clipId, { scale: 1, font_size: newFontSize });
    },
    [clipId]
  );

  const onTextColorChange = useCallback(
    (value?: string) => {
      updateCaptionStyles(clipId, {
        pageStyle: {
          ...clipData.asset_metadata.caption_styles?.pageStyle,
          color: value
        },
        wordStyle: {
          ...clipData.asset_metadata.caption_styles?.wordStyle,
          inactive: {
            ...clipData.asset_metadata.caption_styles?.wordStyle?.inactive,
            color: value
          }
        }
      });
    },
    [clipData.asset_metadata.caption_styles?.pageStyle, clipData.asset_metadata.caption_styles?.wordStyle, clipId]
  );

  const toggleCaptionCaps = useCallback(() => {
    updateCaptionStyles(clipId, { caps: !clipData.asset_metadata.caption_styles?.caps });
  }, [clipId, clipData.asset_metadata.caption_styles?.caps]);

  const isCapsLock = useMemo(() => {
    return !!clipData.asset_metadata.caption_styles?.caps;
  }, [clipData.asset_metadata.caption_styles?.caps]);

  const updateCaptionLineConfig = useCallback(
    (value: CaptionLineConfig) => {
      updateCaptionStyles(clipId, { line_config: value });
    },
    [clipId]
  );

  return (
    <Popover className="relative">
      {({ open }) => (
        <>
          <Popover.Button
            data-testid="captions-menu-typography-button"
            className={classnames(
              'group/button flex size-10 items-center justify-center rounded-md hover:bg-slate-100',
              open ? 'bg-slate-100' : 'hover:bg-slate-50'
            )}
          >
            <div className="flex w-10 cursor-pointer items-center justify-center rounded-md">
              <IconTypography size={16} />
              <PlayerMenuTooltip content="Typography" />
            </div>
          </Popover.Button>
          <Transition
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            <Popover.Panel className="absolute z-50 mt-1 w-64 translate-y-1 space-y-5">
              <div className="space-y-4 rounded-lg border bg-white p-4 shadow-lg">
                {/* Words */}
                <div className="grid grid-cols-12 items-center justify-between gap-2">
                  <span className="col-span-3 text-sm font-medium">Words</span>
                  <div className="group/button relative col-span-9 flex items-center justify-end rounded-md text-sm">
                    <select
                      className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                      value={clipData.asset_metadata.caption_styles?.line_config || CaptionLineConfig.Default}
                      onChange={e => updateCaptionLineConfig(e.target.value as CaptionLineConfig)}
                    >
                      {CAPTION_LINE_CONFIG_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Font Family */}
                <div className="grid grid-cols-12 items-center justify-between gap-2">
                  <span className="col-span-3 text-sm font-medium">Font</span>
                  <div className="group/button relative col-span-9 cursor-pointer rounded-md text-sm">
                    <FontSelector isInline={true} />
                  </div>
                </div>

                {/* Font color */}
                <div className="grid grid-cols-12 items-center justify-between gap-2">
                  <span className="col-span-3 text-sm font-medium">Color</span>
                  <ColorPicker
                    defaultColors={brandKitStore.colors}
                    defaultColorsLabel="Text Color"
                    onChange={onTextColorChange}
                    hex={clipData.asset_metadata.caption_styles?.wordStyle?.inactive?.color || '#ffffff'}
                    children={
                      <div className="flex size-full items-center justify-between bg-white p-2">
                        <div
                          className="size-6 rounded border"
                          style={{
                            background: clipData.asset_metadata.caption_styles?.wordStyle?.inactive?.color || '#ffffff'
                          }}
                        />
                        <span className="font-mono text-xs text-slate-400">
                          {clipData.asset_metadata.caption_styles?.wordStyle?.inactive?.color || '#ffffff'}
                        </span>
                      </div>
                    }
                    classNames="relative col-span-9 flex w-full items-center justify-between rounded-md border overflow-hidden"
                  />
                </div>

                {/* Font Size */}
                <div className="grid grid-cols-12 items-center justify-between gap-2">
                  <span className="col-span-3 text-sm font-medium">Size</span>
                  <span className="col-span-9 flex items-center gap-2">
                    <input
                      type="range"
                      min={20}
                      max={160}
                      value={clipData.asset_metadata.caption_styles?.font_size}
                      step={1}
                      onChange={onFontSizeChange}
                      onMouseUp={onFontSizeChangeComplete}
                      onTouchEnd={onFontSizeChangeComplete}
                      className="h-1 w-full cursor-pointer appearance-none rounded-xl bg-slate-300"
                    />
                    <span className="ml-1 w-6 shrink-0 text-right text-2xs tabular-nums text-slate-400">
                      {clipData.asset_metadata.caption_styles?.font_size}px
                    </span>
                  </span>
                </div>

                {/* Case */}
                <div className="grid grid-cols-12 items-center justify-between gap-2">
                  <span className="col-span-3 text-sm font-medium">Uppercase</span>
                  <div className="group/button relative col-span-9 flex items-center justify-end rounded-md text-sm">
                    <Toggle on={isCapsLock} onToggle={toggleCaptionCaps} />
                  </div>
                </div>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
}

export default memo(CaptionTypographyPopover);
